import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import { Button, Input } from "@/components/primitives";
import BrandHero from "@/components/BrandHero";
import { GoogleButton } from "@/components/BrandLogos";

export default function Login() {
  const { login, googleLogin } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const justUpdated = params.get("updated") === "1";
  // After login, return the user to where they were headed (e.g. the chat PWA sends
  // them to /team-chat via Protected's `from` state), defaulting to home. Guard against
  // open redirects by only honoring internal paths.
  const rawNext = location.state?.from
    ? `${location.state.from.pathname}${location.state.from.search || ""}`
    : params.get("next");
  const nextPath = rawNext && rawNext.startsWith("/") && !rawNext.startsWith("//") ? rawNext : "/";
  const [email, setEmail] = useState(location.state?.email || "");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [socialHint, setSocialHint] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSocialHint("");
    try {
      const { restored } = await login(email.trim(), password);
      if (restored) toast.success("Welcome back, your account deletion was cancelled and your account is restored.");
      nav(nextPath, { replace: true });
    } catch (err) {
      const d = err?.data?.detail;
      if (d?.code === "use_social") {
        setSocialHint(d.message);
        toast.info(d.message);
      } else {
        toast.error(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface flex">
      <div className="hidden lg:block w-1/2 relative">
        <BrandHero />
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <img src="/brand/wordmark-horizontal-dark.png" alt="Two Degrees" className="w-44 h-auto mb-6 lg:hidden" data-testid="login-brand-mark" />
          <h1 className="font-serif text-4xl text-ink mb-1">Welcome back</h1>
          <p className="text-muted-foreground font-sans mb-8">Sign in to your Two Degrees account.</p>

          {justUpdated && (
            <div data-testid="login-updated-notice" className="mb-6 flex items-start gap-2.5 rounded-xl border border-brand/30 bg-brand-tint px-4 py-3">
              <RefreshCw className="w-4 h-4 text-brand mt-0.5 shrink-0" />
              <p className="text-brand-tint-ink text-sm leading-snug">We just updated the app. Please log back in.</p>
            </div>
          )}

          <form onSubmit={submit} className="flex flex-col gap-4">
            <Input label="Email" type="email" data-testid="login-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@work.com" required />
            <Input label="Password" type="password" data-testid="login-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required />
            <div className="flex justify-end -mt-1">
              <Link to="/forgot-password" data-testid="login-forgot-password" className="text-brand text-[13px] font-sans hover:underline">Forgot password?</Link>
            </div>
            <Button type="submit" data-testid="login-submit" loading={loading} size="lg" className="mt-1">Sign in</Button>
          </form>

          <div className="flex items-center gap-3 my-6">
            <div className="h-px bg-divider flex-1" />
            <span className="text-muted-foreground text-xs font-sans">or</span>
            <div className="h-px bg-divider flex-1" />
          </div>

          {socialHint && (
            <p data-testid="login-social-hint" className="text-brand-2 text-sm text-center mb-3 -mt-2">{socialHint}</p>
          )}
          <GoogleButton text="Continue with Google" onClick={() => googleLogin(nextPath)} testID="login-google" />

          <p className="text-center text-muted-foreground text-sm font-sans mt-8">
            New here? <Link to="/register" data-testid="go-register" className="text-brand hover:underline">Create an account</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
